import Axios, { AxiosResponse } from 'axios';

import { config } from '../configs/config';
import { ClaimSummary } from '../types/claimsummary';
import { FavouriteOffers } from '../types/favouriteOffers';
import { InitialMeasurement, InitialMeasurementResponse, Profile, ProfileResponse, WeightMeasurementResponse } from '../types/weightData';
import jwt from 'jsonwebtoken';
import { PDAPassTransactions, Transaction } from '../types/transactions';

function mkPDAHeaders(token: string) {
  return {
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
      'x-auth-token': `${token}`,
    },
  };
}

export async function getUserClaimSummary(token: string, dataPassId: string): Promise<AxiosResponse<ClaimSummary[]>> {
  const issuer = getIssuer(token);

  return (
    Axios.get<ClaimSummary[]>(
      `https://${issuer}/api/v2.6/data/${config.namespace}/${dataPassId}/claim-summary`,
      mkPDAHeaders(token),
    )
  );
}

export async function getFavouriteOffers(token: string): Promise<AxiosResponse<FavouriteOffers[]>> {
  const issuer = getIssuer(token);

  return (
    Axios.get<FavouriteOffers[]>(
      `https://${issuer}/api/v2.6/data/${config.namespace}/perks/favorites?ordering=descending&orderBy=dateCreated&take=1&skip=0`,
      mkPDAHeaders(token),
    )
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateFavouriteOffer(token: string, faves: string): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);

  return (
    Axios.put(
      `https://${issuer}/api/v2.6/data`,
      faves,
      mkPDAHeaders(token)
    )
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function setFavouriteOffer(token: string, faves: string): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);

  return (
    Axios.post(
      `https://${issuer}/api/v2.6/data/${config.namespace}/perks/favorites`,
      faves,
      mkPDAHeaders(token),
    ));
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function setStartingProfile(token: string, profile: Profile): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);

  return (Axios.post(
    `https://${issuer}/api/v2.6/data/${config.namespace}/profile`,
    profile,
    mkPDAHeaders(token)
  ));
}

export async function setInitialMeasurement(token: string, initialMeasurement: InitialMeasurement): Promise<AxiosResponse<any>> {
  const pdaHeaders = mkPDAHeaders(token);
  const issuer = getIssuer(token);

  return (Axios.post(
    `https://${issuer}/api/v2.6/data/${config.namespace}/initialmeasurement`,
    initialMeasurement,
    pdaHeaders
  ));
}

export async function getProfile(): Promise<AxiosResponse<any>> {
  const token = getToken();

  return getStartingProfile(token);
}

export async function getStartingProfile(token: string): Promise<AxiosResponse<ProfileResponse[]>> {
  const issuer = getIssuer(token);

  return (
    Axios.get(
      `https://${issuer}/api/v2.6/data/${config.namespace}/profile`,
      mkPDAHeaders(token),
    ));
}

export async function updateStartingProfile(token: string, body: string): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);

  return (
    Axios.put(
      `https://${issuer}/api/v2.6/data`,
      body,
      mkPDAHeaders(token),
    ));
}


export async function getInitialMeasurement(token: string): Promise<AxiosResponse<InitialMeasurementResponse[]>> {
  const issuer = getIssuer(token);
  return (
    Axios.get<InitialMeasurementResponse[]>(
      `https://${issuer}/api/v2.6/data/${config.namespace}/initialmeasurement`,
      mkPDAHeaders(token),
    ));
}


export async function setWeights(token: string, body: string): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);
  return (
    Axios.post(
      `https://${issuer}/api/v2.6/data/${config.namespace}/measurements`,
      body,
      mkPDAHeaders(token),
    ));
}

export async function getWeights(token: string): Promise<AxiosResponse<WeightMeasurementResponse[]>> {
  const issuer = getIssuer(token);
  return (
    Axios.get<WeightMeasurementResponse[]>(
      `https://${issuer}/api/v2.6/data/${config.namespace}/measurements`,
      mkPDAHeaders(token),
    ));
}

export async function updateWeights(token: string, body: string): Promise<AxiosResponse<WeightMeasurementResponse[]>> {
  const issuer = getIssuer(token);
  return (
    Axios.put<WeightMeasurementResponse[]>(
      `https://${issuer}/api/v2.6/data`,
      body,
      mkPDAHeaders(token),
    ));
}


// Scan transactions
export async function getPDATransactions(token: string): Promise<AxiosResponse<PDAPassTransactions[]>> {
  const issuer = getIssuer(token);
  return (
    Axios.get(
      `https://${issuer}/api/v2.6/data/${config.dsNamespace}/scantransactions`,
      mkPDAHeaders(token),
    ));
}

export async function updatePDATransactions(token: string, data: string): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);

  return (
    Axios.put(
      `https://${issuer}/api/v2.6/data`,
      data,
      mkPDAHeaders(token),
    ));
}


export async function setPDATransactions(token: string, body: Transaction[]): Promise<AxiosResponse<any>> {
  const issuer = getIssuer(token);
  return (
    Axios.post(
      `https://${issuer}/api/v2.6/data/${config.dsNamespace}/scantransactions`,
      [body],
      mkPDAHeaders(token),
    ));
}


// Utility functions
export function getIssuer(token: string): (string | undefined) {
  const decodedJwt = jwt.decode(token);

  if (typeof decodedJwt === 'object') {
    return (decodedJwt ? decodedJwt.iss : undefined)
  }
  else {
    return undefined;
  }

}

export function getToken(): string {
  const token = localStorage.getItem(config.jwtTokenKey) || sessionStorage.getItem(config.jwtTokenKey);
  if (token) {
    return token;
  }
  else {
    return '';
  }
}

